import { toast } from 'svelte-sonner';
import { get } from 'svelte/store';
import type { Task, TaskCopyPreferences, TaskPriority } from './types';
import { DEFAULT_TASK_COPY_PREFERENCES, taskCopyPreferences } from './stores/taskCopyPreferences';

/**
 * Export tasks as plain text for pasting into docs, chats, or LLM prompts.
 * Callers pass the tasks in the order they should appear (usually the current
 * sorted/grouped view order) and are responsible for filtering to incomplete.
 */

export type TaskCopyFormat = 'markdown' | 'bulleted';

export const TASK_COPY_FORMAT_LABELS: Record<TaskCopyFormat, string> = {
  markdown: 'Markdown',
  bulleted: 'Bulleted list',
};

export type TaskCopyField = keyof TaskCopyPreferences;

/** Fields grouped under "Details" in the copy settings, in output order. */
export const TASK_COPY_DETAIL_FIELDS = ['priority', 'created_at', 'updated_at'] as const;

export const TASK_COPY_FIELD_LABELS: Record<TaskCopyField, string> = {
  description: 'Description',
  tags: 'Tags',
  priority: 'Priority',
  created_at: 'Created',
  updated_at: 'Last Updated',
};

const TASK_COPY_FIELD_ORDER: TaskCopyField[] = ['description', 'tags', ...TASK_COPY_DETAIL_FIELDS];

/** Short summary of the included fields, e.g. "Description, Priority". */
export function describeTaskCopyFields(options: TaskCopyPreferences): string {
  const included = TASK_COPY_FIELD_ORDER.filter((field) => options[field]);
  if (included.length === TASK_COPY_FIELD_ORDER.length) return 'All fields';
  if (included.length === 0) return 'Titles only';
  return included.map((field) => TASK_COPY_FIELD_LABELS[field]).join(', ');
}

const PRIORITY_TEXT: Record<Exclude<TaskPriority, 'none'>, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** Detail lines as [label, value] pairs; "no priority" is left out. */
function detailEntries(task: Task, options: TaskCopyPreferences): [string, string][] {
  const entries: [string, string][] = [];
  if (options.priority && task.priority !== 'none') {
    entries.push(['Priority', PRIORITY_TEXT[task.priority] ?? task.priority]);
  }
  const created = options.created_at ? formatTimestamp(task.createdAt) : null;
  if (created) entries.push(['Created', created]);
  const updated = options.updated_at ? formatTimestamp(task.updatedAt) : null;
  if (updated) entries.push(['Last Updated', updated]);
  return entries;
}

/**
 * Split notes into non-empty lines. Any run of newlines (plus surrounding
 * whitespace) is one break, and a leading bullet marker is dropped so notes that
 * are already a list don't become "- - item".
 */
export function splitDescriptionLines(notes: string | null | undefined): string[] {
  if (!notes) return [];
  return notes
    .split(/\s*[\r\n]+\s*/)
    .map((line) => line.trim().replace(/^[-*•]\s+/, ''))
    .filter((line) => line.length > 0);
}

/** ISO 8601 without milliseconds, in UTC (e.g. 2026-09-15T21:03:42Z). */
export function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function tagNames(task: Task): string[] {
  return (task.tags ?? []).map((t) => t.name);
}

export function formatTasksAsBulletedText(
  tasks: Task[],
  options: TaskCopyPreferences = DEFAULT_TASK_COPY_PREFERENCES,
): string {
  const lines: string[] = [];
  for (const task of tasks) {
    lines.push(`- ${task.title}`);

    const description = options.description ? splitDescriptionLines(task.notes) : [];
    if (description.length > 0) {
      lines.push('  - Description');
      for (const line of description) lines.push(`    - ${line}`);
    }

    const tags = options.tags ? tagNames(task) : [];
    if (tags.length > 0) lines.push(`  - Tags: ${tags.join(', ')}`);

    for (const [label, value] of detailEntries(task, options)) {
      lines.push(`  - ${label}: ${value}`);
    }
  }
  return lines.join('\n');
}

/**
 * One `##` section per task. Notes are kept verbatim (trimmed) since they are
 * often already markdown. `heading` becomes a `#` title above the tasks.
 */
export function formatTasksAsMarkdown(
  tasks: Task[],
  heading?: string,
  options: TaskCopyPreferences = DEFAULT_TASK_COPY_PREFERENCES,
): string {
  const blocks: string[] = [];
  if (heading) blocks.push(`# ${heading}`);

  for (const task of tasks) {
    const section: string[] = [`## ${task.title}`];

    const notes = options.description ? task.notes?.trim() : null;
    if (notes) section.push(`### Description\n\n${notes}`);

    const tags = options.tags ? tagNames(task) : [];
    if (tags.length > 0) section.push(`### Tags\n\n${tags.map((t) => `- ${t}`).join('\n')}`);

    const details = detailEntries(task, options).map(
      ([label, value]) => `- **${label}:** ${value}`,
    );
    if (details.length > 0) section.push(`### Details\n\n${details.join('\n')}`);

    blocks.push(section.join('\n\n'));
  }
  return blocks.join('\n\n') + '\n';
}

export function formatTasks(
  tasks: Task[],
  format: TaskCopyFormat,
  heading?: string,
  options: TaskCopyPreferences = DEFAULT_TASK_COPY_PREFERENCES,
): string {
  return format === 'markdown'
    ? formatTasksAsMarkdown(tasks, heading, options)
    : formatTasksAsBulletedText(tasks, options);
}

/** Copy text, falling back to execCommand for non-HTTPS / older browsers. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}

/**
 * Format with the user's copy settings, copy, and toast. Shared by the
 * view-options menu and group menus.
 */
export async function copyTasksWithToast(
  tasks: Task[],
  format: TaskCopyFormat,
  heading?: string,
): Promise<void> {
  if (tasks.length === 0) {
    toast.info('No incomplete tasks to copy');
    return;
  }
  const options = get(taskCopyPreferences);
  const ok = await copyTextToClipboard(formatTasks(tasks, format, heading, options));
  const noun = tasks.length === 1 ? 'task' : 'tasks';
  const titlesOnly = TASK_COPY_FIELD_ORDER.every((field) => !options[field])
    ? ' (titles only)'
    : '';
  if (ok) {
    toast.success(
      `Copied ${tasks.length} ${noun} as ${TASK_COPY_FORMAT_LABELS[format]}${titlesOnly}`,
    );
  } else {
    toast.error('Could not copy to clipboard');
  }
}
